import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  carrierDetailInitialState,
  initialCarrierBookingsListState,
  initialCarrierComplianceState,
  initialCarrierDriversListState,
  initialCarrierEquipmentListState,
} from "./carrier-detail.models";
import {
  fetchCarrierBookings,
  fetchCarrierCompliance,
  fetchCarrierDetail,
  fetchCarrierDrivers,
  fetchCarrierEquipment,
} from "./carrier-detail.thunk";

const carrierDetailSlice = createSlice({
  name: "carrierDetail",
  initialState: carrierDetailInitialState,
  reducers: {
    setSelectedCarrierId: (state, action: PayloadAction<string | null>) => {
      const next = action.payload;
      if (state.selectedCarrierId !== next) {
        state.driversList = { ...initialCarrierDriversListState };
        state.compliance = { ...initialCarrierComplianceState };
        state.equipmentList = { ...initialCarrierEquipmentListState };
        state.bookingsList = { ...initialCarrierBookingsListState };
        if (next != null) {
          state.carrier = null;
          state.loading = true;
          state.error = null;
        }
      }
      state.selectedCarrierId = next;
    },
    clearCarrierDetail: (state) => {
      state.carrier = null;
      state.error = null;
      state.selectedCarrierId = null;
      state.driversList = { ...initialCarrierDriversListState };
      state.compliance = { ...initialCarrierComplianceState };
      state.equipmentList = { ...initialCarrierEquipmentListState };
      state.bookingsList = { ...initialCarrierBookingsListState };
    },
    clearCarrierDetailError: (state) => {
      state.error = null;
    },
    setCarrierDriversListRange: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      state.driversList.fromIndex = action.payload.fromIndex;
      state.driversList.toIndex = action.payload.toIndex;
    },
    setCarrierDriversSearch: (state, action: PayloadAction<string>) => {
      state.driversList.search = action.payload;
    },
    setCarrierDriversShowOnlyActive: (state, action: PayloadAction<boolean>) => {
      state.driversList.showOnlyActive = action.payload;
    },
    setCarrierDriversSort: (
      state,
      action: PayloadAction<{ sortField: string; sortOrder: "asc" | "desc" }>,
    ) => {
      state.driversList.sortField = action.payload.sortField;
      state.driversList.sortOrder = action.payload.sortOrder;
    },
    setCarrierEquipmentListRange: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      state.equipmentList.fromIndex = action.payload.fromIndex;
      state.equipmentList.toIndex = action.payload.toIndex;
    },
    setCarrierEquipmentSearch: (state, action: PayloadAction<string>) => {
      state.equipmentList.search = action.payload;
    },
    setCarrierBookingsListRange: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      state.bookingsList.fromIndex = action.payload.fromIndex;
      state.bookingsList.toIndex = action.payload.toIndex;
    },
    setCarrierBookingsSearch: (state, action: PayloadAction<string>) => {
      state.bookingsList.search = action.payload;
    },
    setCarrierBookingsDateRange: (
      state,
      action: PayloadAction<{ dateFrom: string | null; dateTo: string | null }>,
    ) => {
      state.bookingsList.dateFrom = action.payload.dateFrom;
      state.bookingsList.dateTo = action.payload.dateTo;
    },
    setCarrierBookingsStatusFilters: (state, action: PayloadAction<string[]>) => {
      state.bookingsList.statusFilterValues = action.payload;
    },
    setCarrierBookingsSort: (
      state,
      action: PayloadAction<{ sortingField: string; sortingOrder: "asc" | "desc" }>,
    ) => {
      state.bookingsList.sortingField = action.payload.sortingField;
      state.bookingsList.sortingOrder = action.payload.sortingOrder;
    },
    resetCarrierBookingsList: (state) => {
      state.bookingsList = { ...initialCarrierBookingsListState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCarrierDetail.pending, (state, action) => {
        const requestedId = action.meta.arg.carrierId;
        const currentCarrierId =
          state.carrier != null ? String(state.carrier.carrierId) : null;
        if (currentCarrierId !== requestedId) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchCarrierDetail.fulfilled, (state, action) => {
        const requestedId = action.meta.arg.carrierId;
        if (state.selectedCarrierId !== requestedId) {
          return;
        }
        state.loading = false;
        state.carrier = action.payload.data.carrier;
      })
      .addCase(fetchCarrierDetail.rejected, (state, action) => {
        const requestedId = action.meta.arg.carrierId;
        if (state.selectedCarrierId !== requestedId) {
          return;
        }
        state.loading = false;
        const message = action.payload ?? "Failed to fetch carrier detail";
        if (!state.carrier) {
          state.error = message;
        }
      })
      .addCase(fetchCarrierDrivers.pending, (state) => {
        state.driversList.loading = true;
        state.driversList.error = null;
      })
      .addCase(fetchCarrierDrivers.fulfilled, (state, action) => {
        state.driversList.loading = false;
        state.driversList.rows = action.payload.data.drivers;
        state.driversList.totalCount = action.payload.data.totalCount;
      })
      .addCase(fetchCarrierDrivers.rejected, (state, action) => {
        state.driversList.loading = false;
        state.driversList.error = action.payload ?? "Failed to fetch drivers";
        state.driversList.rows = [];
        state.driversList.totalCount = 0;
      })
      .addCase(fetchCarrierCompliance.pending, (state) => {
        state.compliance.loading = true;
      })
      .addCase(fetchCarrierCompliance.fulfilled, (state, action) => {
        state.compliance.loading = false;
        state.compliance.data = action.payload.data.compliance;
      })
      .addCase(fetchCarrierCompliance.rejected, (state) => {
        // FR-AV213-005: failure → empty (null); no user-facing error toast/inline.
        state.compliance.loading = false;
        state.compliance.data = null;
      })
      .addCase(fetchCarrierEquipment.pending, (state, action) => {
        if (state.selectedCarrierId !== action.meta.arg.carrierId) {
          return;
        }
        state.equipmentList.loading = true;
        state.equipmentList.error = null;
      })
      .addCase(fetchCarrierEquipment.fulfilled, (state, action) => {
        if (state.selectedCarrierId !== action.meta.arg.carrierId) {
          return;
        }
        state.equipmentList.loading = false;
        state.equipmentList.rows = action.payload.data.items;
        state.equipmentList.totalCount = action.payload.data.totalCount;
      })
      .addCase(fetchCarrierEquipment.rejected, (state, action) => {
        if (state.selectedCarrierId !== action.meta.arg.carrierId) {
          return;
        }
        state.equipmentList.loading = false;
        state.equipmentList.error = action.payload ?? "Failed to fetch equipment";
        state.equipmentList.rows = [];
        state.equipmentList.totalCount = 0;
      })
      .addCase(fetchCarrierBookings.pending, (state, action) => {
        if (state.selectedCarrierId !== action.meta.arg.carrierId) {
          return;
        }
        state.bookingsList.loading = true;
        state.bookingsList.error = null;
      })
      .addCase(fetchCarrierBookings.fulfilled, (state, action) => {
        if (state.selectedCarrierId !== action.meta.arg.carrierId) {
          return;
        }
        state.bookingsList.loading = false;
        state.bookingsList.rows = action.payload.rows;
        state.bookingsList.totalCount = action.payload.totalCount;
      })
      .addCase(fetchCarrierBookings.rejected, (state, action) => {
        if (state.selectedCarrierId !== action.meta.arg.carrierId) {
          return;
        }
        state.bookingsList.loading = false;
        state.bookingsList.error = action.payload ?? "Failed to fetch bookings";
        state.bookingsList.rows = [];
        state.bookingsList.totalCount = 0;
      });
  },
});

export const {
  setSelectedCarrierId,
  clearCarrierDetail,
  clearCarrierDetailError,
  setCarrierDriversListRange,
  setCarrierDriversSearch,
  setCarrierDriversShowOnlyActive,
  setCarrierDriversSort,
  setCarrierEquipmentListRange,
  setCarrierEquipmentSearch,
  setCarrierBookingsListRange,
  setCarrierBookingsSearch,
  setCarrierBookingsDateRange,
  setCarrierBookingsStatusFilters,
  setCarrierBookingsSort,
  resetCarrierBookingsList,
} = carrierDetailSlice.actions;

export default carrierDetailSlice.reducer;
